<template>
  <Modal
    :show="showModal"
    size="sm"
    @close="closeModal"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    headerVariant="blue"
  >
    <template #header>
      <div class="bucket-header__text">
        <h2 class="bucket-header__title">{{ props.title }}</h2>
        <p class="bucket-header__subtitle">{{ props.subtitle }}</p>
      </div>
      <button type="button" class="bucket-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <Form ref="formRef" class="form" @submit="addBucket">
      <div class="form-group">
        <Select
          v-model="addBucketObj.bucketOptionId"
          label="Bucket"
          placeholder="Select Bucket"
          :options="bucketSelectOptions"
          :required="true"
          :error="bucketError"
          @change="handleBucketChange"
          @blur="handleBucketBlur"
          size="sm"
        />
      </div>
      <div class="form-group">
        <Select
          v-model="addBucketObj.taskType"
          label="Task Type"
          placeholder="Select Task Type"
          :options="taskTypeOptions"
          :required="!isDoNotContactSelected"
          :error="taskTypeError"
          :disabled="isDoNotContactSelected"
          @change="handleTaskTypeChange"
          @blur="handleTaskTypeBlur"
          size="sm"
        />
      </div>
      <div class="form-group">
        <label for="followUpDate" class="date-label">
          Follow up Date
          <span v-if="!isDoNotContactSelected" class="required-field">*</span>
        </label>
        <DateSelector
          v-model="addBucketObj.followUpDate"
          :disable-past="false"
          :disabled="isDoNotContactSelected"
          @update:model-value="handleDateChange"
          @blur="handleDateBlur"
        />
        <div v-if="dateError" class="error-message">{{ dateError }}</div>
      </div>
    </Form>

    <template #footer>
      <div class="modal-footer-buttons">
        <Button
          type="button"
          variant="secondary"
          size="md"
          @click="closeModal"
          :disabled="isLoading"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          @click="handleSaveClick"
          :loading="isLoading"
          :disabled="isLoading"
        >
          Save
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Modal, Button, Icon, DateSelector, Select } from "@/components/ui";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";
import { Form } from "vee-validate";
import { useRegisterBucket } from "@/composables/bucket";
import { useBucketOptions } from "@/composables/bucket";
import { useBucketLockStatus } from "@/composables/bucket";
const props = defineProps({
  contactIds: {
    type: [Array, String],
    default: () => [],
  },
  title: {
    type: String,
    default: "Move Contact",
  },
  subtitle: {
    type: String,
    default: "Assign contact to a bucket",
  },
});

const emit = defineEmits(["closeAddBucketModal", "refreshBuckets"]);

// Get global properties
const $toast = useToast();

// Store
const store = useStore();
const account = computed(() => store.getters.account.data);
const organizationId = computed(() => account.value?.organizationId);

// Modal state
const showModal = ref(true);
const formRef = ref(null);

// Form state
const addBucketObj = ref({
  taskType: null,
  bucketOptionId: null,
  followUpDate: null,
  contactIds: [],
  organizationId: null,
});

// Validation state
const taskTypeError = ref(null);
const bucketError = ref(null);
const dateError = ref(null);
const taskTypeTouched = ref(false);
const bucketTouched = ref(false);
const dateTouched = ref(false);

// Bucket options query
const bucketOptionsParams = computed(() => ({
  organizationId: organizationId.value,
}));
const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

// Bucket lock status query
const bucketLockParams = computed(() => {
  if (!organizationId.value) return null;
  return {
    organizationId: organizationId.value,
    offset: new Date().getTimezoneOffset(),
  };
});
const { data: bucketLockStatusData } = useBucketLockStatus(bucketLockParams);

// Register bucket mutation
const { mutateAsync: registerBucketMutation, isPending: isLoading } = useRegisterBucket();

// Computed
const taskTypeLabelMap = {
  [TaskType.call]: "Call",
  [TaskType.email]: "Email",
  [TaskType.linkedIn]: "LinkedIn",
  [TaskType.whatsApp]: "WhatsApp",
  [TaskType.sms]: "SMS",
  [TaskType.other]: "Other",
};

const taskTypeOptions = computed(() => {
  return Object.values(TaskType).map((task) => ({
    value: task,
    label: taskTypeLabelMap[task] || task,
    icon: getTaskTypeIcon(task),
  }));
});

const bucketOptions = computed(() => bucketOptionsData.value || []);
const bucketLockStatus = computed(() => bucketLockStatusData.value);

// Get set of locked bucket IDs
const lockedBucketIds = computed(() => {
  if (!bucketLockStatus.value?.enabled) return new Set();
  return new Set(
    bucketLockStatus.value.bucketStatuses
      .filter((s) => s.isLocked && !s.hasOverride)
      .map((s) => s.bucketOptionId)
  );
});

const bucketSelectOptions = computed(() => {
  return bucketOptions.value.map((bucketOption) => {
    const isLocked = lockedBucketIds.value.has(bucketOption.id);
    return {
      value: bucketOption.id,
      label: isLocked ? `🔒 ${bucketOption.name} (Locked)` : bucketOption.name,
      disabled: isLocked,
      icon: "fa6-solid:bucket",
      iconColor: bucketOption.colorHex,
    };
  });
});

// The "Do Not Contact" bucket has no future outreach — task type and follow-up
// date don't apply, so they're disabled and cleared once it's selected.
const isDoNotContactSelected = computed(() => {
  const selected = bucketOptions.value.find(
    (bucketOption) => bucketOption.id === addBucketObj.value.bucketOptionId
  );
  return selected?.name?.trim().toLowerCase() === "do not contact";
});

watch(isDoNotContactSelected, (isDnc) => {
  // Whether entering or leaving "Do Not Contact", the task type never carries
  // over silently — entering clears it because it doesn't apply, and leaving
  // clears it too so a value picked while DNC was active (or auto-cleared)
  // can't be submitted for a bucket that actually needs one.
  addBucketObj.value.taskType = null;
  taskTypeTouched.value = false;
  taskTypeError.value = null;
  if (isDnc) {
    addBucketObj.value.followUpDate = null;
    dateError.value = null;
  }
});

// Methods
const setValues = () => {
  // Handle both array and single contactId
  if (Array.isArray(props.contactIds)) {
    addBucketObj.value.contactIds = props.contactIds;
  } else if (props.contactIds) {
    addBucketObj.value.contactIds = [props.contactIds];
  } else {
    addBucketObj.value.contactIds = [];
  }
  addBucketObj.value.organizationId = organizationId.value;
};

const closeModal = () => {
  emit("closeAddBucketModal");
};

const handleSaveClick = () => {
  addBucket();
};

const validateTaskType = (force = false) => {
  if (isDoNotContactSelected.value) {
    taskTypeError.value = null;
    return;
  }
  if (!force && !taskTypeTouched.value) return;
  if (!addBucketObj.value.taskType) {
    taskTypeError.value = "Task Type is required";
  } else {
    taskTypeError.value = null;
  }
};

const validateBucket = (force = false) => {
  if (!force && !bucketTouched.value) return;
  if (!addBucketObj.value.bucketOptionId) {
    bucketError.value = "Bucket is required";
  } else if (lockedBucketIds.value.has(addBucketObj.value.bucketOptionId)) {
    bucketError.value = "Cannot move to a locked bucket";
  } else {
    bucketError.value = null;
  }
};

const validateDate = (force = false) => {
  if (isDoNotContactSelected.value) {
    dateError.value = null;
    return;
  }
  if (!force && !dateTouched.value) return;
  if (!addBucketObj.value.followUpDate) {
    dateError.value = "Follow up Date is required";
  } else {
    dateError.value = null;
  }
};

const handleTaskTypeChange = () => {
  taskTypeTouched.value = true;
  validateTaskType();
};

const handleBucketChange = () => {
  bucketTouched.value = true;
  validateBucket();
};

const handleTaskTypeBlur = () => {
  taskTypeTouched.value = true;
  validateTaskType();
};

const handleBucketBlur = () => {
  bucketTouched.value = true;
  validateBucket();
};

const handleDateChange = () => {
  dateTouched.value = true;
  validateDate();
};

const handleDateBlur = () => {
  dateTouched.value = true;
  validateDate();
};

const addBucket = async () => {
  taskTypeTouched.value = true;
  bucketTouched.value = true;
  dateTouched.value = true;

  validateTaskType(true);
  validateBucket(true);
  validateDate(true);

  if (taskTypeError.value || bucketError.value || dateError.value) {
    return;
  }

  try {
    const rawDate = addBucketObj.value.followUpDate;
    const payload = {
      ...addBucketObj.value,
      followUpDate:
        rawDate instanceof Date
          ? new Date(
              rawDate.getFullYear(),
              rawDate.getMonth(),
              rawDate.getDate(),
              0,
              0,
              0,
              0
            ).toISOString()
          : rawDate,
    };

    await registerBucketMutation(payload);
    $toast?.success("Bucket moved successfully!");
    emit("refreshBuckets");
    closeModal();
  } catch (error) {
    console.error("Error registering bucket:", error);
    $toast?.error(
      error?.response?.data?.msg || error?.message || "Failed to move bucket. Please try again."
    );
  }
};

// Lifecycle
onMounted(() => {
  setValues();
});
</script>

<style scoped>
.bucket-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bucket-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}

.bucket-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.bucket-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.bucket-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.form {
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group :deep(.ui-select__field),
.form-group :deep(.ui-input__field) {
  height: 40px !important;
  min-height: 40px !important;
}

.form-group :deep(.ui-select__container),
.form-group :deep(.ui-input__container) {
  height: 40px;
  min-height: 40px;
}

.date-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
}

.required-field {
  color: #dc3545;
  font-size: 1.1rem;
  margin-left: 2px;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 6px;
  font-weight: 400;
}

:deep(.ui-select__error) {
  font-size: 0.875rem !important;
}

.modal-footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}
</style>
